import React from 'react';
import { useFormik } from 'formik';

import Yup from '~yup';
import UtilsFunctions from '~utils/functions';

import * as Styles from './_productSettingCreateProductForm.module.scss';
import LabelledInput from '~components/atoms/LabelledInput';
import Checkbox from '~components/atoms/Checkbox';
import dayjs from 'dayjs';
import LabelledDropdown from '~components/atoms/LabelledDropdown';
import { AdminSettingsTypeEnum, FundTypeEnum, GoldTypeEnum, ProductStatusEnum, MetalTypeEnum, SilverTypeEnum, SilverGoldTypeEnum } from '~utils/enums';
import ActionButton from '~components/atoms/ActionButton';
import { useAppDispatch } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';

const ProductSettingCreateProductForm = () => {
    const dispatch = useAppDispatch();

    const {
        Misc:
            {
                blockEnterKey,
                dateTimeFormat,
            },
    } = UtilsFunctions;

    const ProductSettingCreateProductFormValidation = () => Yup.object().shape({
        productName: Yup.string().required('Product Name is required'),
        physicalSilverWeight: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            })
            .test('no-exponential-notation',
                'Physical Gold Weight must be a whole number (exponential notation is not allowed)',
                function (value) {
                    const original = this.originalValue;
                    if (original === undefined || original === null || original === '') return true;
                    return !/e/i.test(String(original));
                }
            )
            .when([], {
                is: () => !formik.values.goldTypeToggle,
                then: (schema) => schema.required('Physical Weight is required'),
                otherwise: (schema) => schema.notRequired().nullable(),
            }),
        goldPriceBankBuy: Yup.number().min(0).typeError('Value must be a number')
        //set input is required only if goldPriceAPI is toggle off
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            })
            .when([], {
                is: () => !formik.values.goldPriceBankBuyApi,
                then: (schema) => schema.required('Price Bank Buy is required'),
                otherwise: (schema) => schema.notRequired().nullable(),
            })
            .test('no-exponential-notation',
                'Gold Price Bank Buy must be a whole number (exponential notation is not allowed)',
                function (value) {
                    const original = this.originalValue;
                    if (original === undefined || original === null || original === '') return true;
                    return !/e/i.test(String(original));
                }
            ),
        goldPriceBankSell: Yup.number().min(0).typeError('Value must be a number')
        //set input is required only if goldPriceAPI is toggle off
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            })
            .when([], {
                is: () => !formik.values.goldPriceBankSellApi,
                then: (schema) => schema.required('Price Bank Sell is required'),
                otherwise: (schema) => schema.notRequired().nullable(),
            })
            .test('no-exponential-notation',
                'Gold Price Bank Sell must be a whole number (exponential notation is not allowed)',
                function (value) {
                    const original = this.originalValue;
                    if (original === undefined || original === null || original === '') return true;
                    return !/e/i.test(String(original));
                }
            ),
        fundType: Yup.string()
            .test('check-branch-name',
                'Selected fund type does not exist',
                function (value) {
                    if (!value) return false;

                    return !!Object.keys(FundTypeEnum).find((fundType) => fundType === value);
                }
            ).required('Fund Type is required'),
        metalType: Yup.string()
            .required('Metal Type is required')
            .oneOf(Object.keys(MetalTypeEnum).filter(k => isNaN(Number(k))),
                'Invalid Metal Type'),
        // goldType: Yup.string()
        //     .test('check-branch-name',
        //         'Selected gold type does not exist',
        //         function (value) {
        //             if (!value) return false;

        //             return !!Object.keys(GoldTypeEnum).find((goldType) => goldType === value.replace(/\s+(.)/g, (_, char) => char.toUpperCase()));
        //         }
        //     ).required(),
        affinBankBuySplit: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            }).required('Affin Bank Buy Split is required')
            .test('no-exponential-notation',
                'Affin Bank Buy Split must be a whole number (exponential notation is not allowed)',
                function (value) {
                    const original = this.originalValue;
                    if (original === undefined || original === null || original === '') return true;
                    return !/e/i.test(String(original));
                }
            ),
        tttBankBuySplit: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            }).required('TTT Bank Buy Split is required')
            .test('no-exponential-notation',
                'TTT Bank Buy Split must be a whole number (exponential notation is not allowed)',
                function (value) {
                    const original = this.originalValue;
                    if (original === undefined || original === null || original === '') return true;
                    return !/e/i.test(String(original));
                }
            ),
        affinBankSellSplit: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            }).required('Affin Bank Sell Split is required')
            .test('no-exponential-notation',
                'Affin Bank Sell Split must be a whole number (exponential notation is not allowed)',
                function (value) {
                    const original = this.originalValue;
                    if (original === undefined || original === null || original === '') return true;
                    return !/e/i.test(String(original));
                }
            ),
        tttBankSellSplit: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            }).required('TTT Bank Sell Split is required')
            .test('no-exponential-notation',
                'TTT Bank Sell Split must be a whole number (exponential notation is not allowed)',
                function (value) {
                    const original = this.originalValue;
                    if (original === undefined || original === null || original === '') return true;
                    return !/e/i.test(String(original));
                }
            ),
        mintingCost: Yup.number().min(0).typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;

                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            }).required('Minting Cost is required')
            .test('no-exponential-notation',
                'Minting Cost must be a whole number (exponential notation is not allowed)',
                function (value) {
                    const original = this.originalValue;
                    if (original === undefined || original === null || original === '') return true;
                    return !/e/i.test(String(original));
                }
            ),
        // lowGoldAlert: Yup.number().min(0).typeError('Value must be a number')
        //     .test('no-space', 'Spaces are not allowed', (value, context) => {
        //         const rawValue = context.originalValue;

        //         if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
        //             return false;
        //         }
        //         return true;
        //     }).required('Low Gold Alert is required')
        //     .test('no-exponential-notation',
        //         'Low Gold Alert must be a whole number (exponential notation is not allowed)',
        //         function (value) {
        //             const original = this.originalValue;
        //             if (original === undefined || original === null || original === '') return true;
        //             return !/e/i.test(String(original));
        //         }
        //     ),

        // toggle checking handler
        goldPriceBankBuyApi: Yup.boolean().required('Must have Bank Buy type'),
        goldPriceBankSellApi: Yup.boolean().required('Must have Bank Sell type'),
        goldTypeToggle: Yup.boolean().required('Must have Gold Type'),
        affinBankBuySplitToggle: Yup.boolean().required('Must have Affin Bank Buy type'),
        tttBankBuySplitToggle: Yup.boolean().required('Must have TTT Bank Buy type'),
        affinBankSellSplitToggle: Yup.boolean().required('Must have Affin Bank Sell type'),
        tttBankSellSplitToggle: Yup.boolean().required('Must have TTT Bank Sell type'),
        mintingCostToggle: Yup.boolean().required('Must have Minting Cost type'),
    });

    const ProductSettingCreateProductInitialValues = {
        productName: '',
        physicalSilverWeight: '',
        fundType: '',
        metalType: '',
        // goldType: '',
        goldPriceBankBuy: '',
        goldPriceBankSell: '',
        affinBankBuySplit: '',
        tttBankBuySplit: '',
        affinBankSellSplit: '',
        tttBankSellSplit: '',
        mintingCost: '',
        // lowGoldAlert: '',

        // toggle checking handler
        goldPriceBankBuyApi: false,
        goldPriceBankSellApi: false,
        goldTypeToggle: false,
        affinBankBuySplitToggle: false,
        tttBankBuySplitToggle: false,
        affinBankSellSplitToggle: false,
        tttBankSellSplitToggle: false,
        mintingCostToggle: false,
    };

    const formik = useFormik({
        initialValues: ProductSettingCreateProductInitialValues,
        validationSchema: ProductSettingCreateProductFormValidation(),
        onSubmit: (values) => {

            const {
                productName,
                physicalSilverWeight,
                fundType,
                // goldType,
                metalType,
                goldPriceBankBuy,
                goldPriceBankSell,
                affinBankBuySplit,
                tttBankBuySplit,
                affinBankSellSplit,
                tttBankSellSplit,
                mintingCost,
                // lowGoldAlert,

                goldPriceBankBuyApi,
                goldPriceBankSellApi,
                goldTypeToggle,
                affinBankBuySplitToggle,
                tttBankBuySplitToggle,
                affinBankSellSplitToggle,
                tttBankSellSplitToggle,
                mintingCostToggle,
            } = values;

            if (values){
                const isGold = MetalTypeEnum[metalType as keyof typeof MetalTypeEnum] === MetalTypeEnum.Gold;
                const isSilver = MetalTypeEnum[metalType as keyof typeof MetalTypeEnum] === MetalTypeEnum.Silver;
                const physicalWeight = !goldTypeToggle ? physicalSilverWeight : undefined;

                dispatch(combinedActions.settingCreateAdminSettingsAttempt({
                    type: AdminSettingsTypeEnum.Product,
                    productName,
                    physicalGoldWeight: isGold ? physicalWeight : undefined,
                    physicalSilverWeight: isSilver ? physicalWeight : undefined,
                    affinBankBuySplitPercentage: affinBankBuySplitToggle ? affinBankBuySplit : undefined,
                    tttBankBuySplitPercentage: tttBankBuySplitToggle ? tttBankBuySplit : undefined,
                    affinBankSellSplitPercentage: affinBankSellSplitToggle ? affinBankSellSplit : undefined,
                    tttBankSellSplitPercentage: tttBankSellSplitToggle ? tttBankSellSplit : undefined,
                    mintingCostPercentage: mintingCostToggle ? mintingCost : undefined,
                    affinBankBuySplit: !affinBankBuySplitToggle ? affinBankBuySplit : undefined,
                    tttBankBuySplit: !tttBankBuySplitToggle ? tttBankBuySplit : undefined,
                    affinBankSellSplit: !affinBankSellSplitToggle ? affinBankSellSplit : undefined,
                    tttBankSellSplit: !tttBankSellSplitToggle ? tttBankSellSplit : undefined,
                    mintingCost: !mintingCostToggle ? mintingCost : undefined,
                    // lowGoldAlert: isGold ? (parseFloat(lowGoldAlert) / 100).toString() : undefined,
                    // lowSilverAlert: isSilver ? (parseFloat(lowGoldAlert) / 100).toString() : undefined,
                    // fixedBankBuyGoldPrice: isGold && !goldPriceBankBuyApi ? goldPriceBankBuy : undefined,

                    fixedBankBuyGoldPrice: isGold ? (goldPriceBankBuyApi ? undefined : goldPriceBankBuy) : undefined,
                    fixedBankSellGoldPrice: isGold ? (goldPriceBankSellApi ? undefined : goldPriceBankSell) : undefined,
                    fixedBankBuySilverPrice: isSilver ? (goldPriceBankBuyApi ? undefined : goldPriceBankBuy) : undefined,
                    fixedBankSellSilverPrice: isSilver ? (goldPriceBankSellApi ? undefined : goldPriceBankSell) : undefined,
                    // fixedBankSellGoldPrice: isGold && !goldPriceBankSellApi ? goldPriceBankSell : undefined,
                    // fixedBankSellSilverPrice: goldPriceBankSellApi ? undefined : goldPriceBankSell,
                    fundType: FundTypeEnum[fundType as keyof typeof FundTypeEnum],
                    silverType: isSilver ? SilverGoldTypeEnum.Physical : undefined,
                    goldType: isGold ? SilverGoldTypeEnum.Physical : undefined,
                    metalType: MetalTypeEnum[metalType as keyof typeof MetalTypeEnum],
                    productStatus: ProductStatusEnum.Active,
                    purity: 99.99,
                    brand: 1,
                    image: 'link',
                    description: '-',
                }));
            }
        },
    });

    const fundTypeOptions = Object.entries(FundTypeEnum)
        .filter(([k]) => isNaN(Number(k)))
        .map(([label, value]) => ({ label, value }));

    const metalTypeOptions = Object.entries(MetalTypeEnum)
        .filter(([k]) => isNaN(Number(k)))
        .map(([label, value]) => ({ label, value }));

    // const goldTypeOptions = Object.entries(GoldTypeEnum)
    //     .filter(([k]) => isNaN(Number(k)))
    //     .map(([label, value]) => ({ label: label.replace(/([a-z])([A-Z])/g, '$1 $2'), value }));

    // const productCategoryOptions = [
    //     {
    //         label: 'Emas-i',
    //         value: 'Emas-i',
    //     },
    //     {
    //         label: 'Emas Account-i',
    //         value: 'Emas Account-i',
    //     },
    //     {
    //         label: 'Precious Metal Pyhsical',
    //         value: 'Precious Metal Pyhsical',
    //     },
    //     {
    //         label: 'Precious Metal Account',
    //         value: 'Precious Metal Account',
    //     },
    // ];

    // const handleChangeGoldType = (e: React.ChangeEvent<HTMLInputElement>) => {
    //     formik.handleChange({ target: { name: 'goldType', value: e.target.value } });

    //     if (e.target.value === 'Paper Gold'){
    //         formik.handleChange({ target: { name: 'goldType', value: 'Paper Gold' } });
    //         formik.setFieldValue('goldTypeToggle', true);
    //     }
    //     if (e.target.value === 'Physical Gold'){
    //         formik.handleChange({ target: { name: 'goldType', value: 'Physical Gold' } });
    //         formik.setFieldValue('goldTypeToggle', false);
    //     }
    // };

    // const handleSelectGoldType = (e: TDropdownOption) => {
    //     if (e.value === GoldTypeEnum.PaperGold){
    //         formik.handleChange({ target: { name: 'goldType', value: 'Paper Gold' } });
    //         formik.setFieldValue('goldTypeToggle', true);
    //     }
    //     if (e.value === GoldTypeEnum.PhysicalGold){
    //         formik.handleChange({ target: { name: 'goldType', value: 'Physical Gold' } });
    //         formik.setFieldValue('goldTypeToggle', false);
    //     }

    //     formik.setFieldTouched('goldType', false);
    // };

    const renderProductSettingForm = () => {
        return (
            <form
                onSubmit={formik.handleSubmit}
                className={Styles['form-container']}
                onKeyDown={blockEnterKey}
            >
                <div className={Styles['form-subcontainer']}>
                    <div>Product ID</div>

                    <LabelledInput
                        id='productId'
                        name='productId'
                        label=':'
                        value={''}
                        disabled
                        isRow
                    />

                    <div>Product Name</div>

                    <LabelledInput
                        id='productName'
                        name='productName'
                        label=':'
                        value={formik.values.productName}
                        error={formik.touched.productName ? formik.errors.productName : undefined}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isRow
                    />

                    <div>Created At</div>

                    <LabelledInput
                        label=':'
                        value={dateTimeFormat(dayjs().toISOString())}
                        disabled
                        isRow
                    />

                    <div>Metal Type</div>

                    <div className={Styles['flex-span-container']}>
                        <LabelledDropdown
                            id='metalType'
                            name='metalType'
                            label=':'
                            options={metalTypeOptions}
                            value={formik.values.metalType || ''}
                            error={formik.touched.metalType ? formik.errors.metalType : undefined}
                            onChange={(e) => formik.handleChange({ target: { name: 'metalType', value: e.target.value } })}
                            noOptionsMessage='No metal type found'
                            autoComplete='off'
                            optionSelected={!!metalTypeOptions.find((option) => option.label === formik.values.metalType)}
                            isRow
                        />
                        <div></div>
                    </div>

                    <div>Fund Type</div>

                    <div className={Styles['flex-span-container']}>
                        <LabelledDropdown
                            id='fundType'
                            name='fundType'
                            label=':'
                            options={fundTypeOptions}
                            value={formik.values.fundType || ''}
                            error={formik.touched.fundType ? formik.errors.fundType : undefined}
                            onChange={(e) => formik.handleChange({ target: { name: 'fundType', value: e.target.value } })}
                            noOptionsMessage='No branches found'
                            autoComplete='off'
                            optionSelected={!!fundTypeOptions.find((option) => option.label === formik.values.fundType)}
                            isRow
                        />

                        <div></div>
                    </div>

                    <div>Physical Weight (gram)</div>

                    <div className={Styles['flex-span-container']}>
                        <LabelledInput
                            id='physicalSilverWeight'
                            name='physicalSilverWeight'
                            label=':'
                            value={formik.values.physicalSilverWeight}
                            error={formik.touched.physicalSilverWeight ? formik.errors.physicalSilverWeight : undefined}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isRow
                            disabled={formik.values.goldTypeToggle}
                        />

                        <div></div>
                    </div>

                    <div>Price Bank Buy (RM)*</div>

                    <div className={Styles['flex-span-container']}>
                        <div>
                            <LabelledInput
                                id='goldPriceBankBuy'
                                name='goldPriceBankBuy'
                                label=':'
                                value={formik.values.goldPriceBankBuy}
                                error={formik.touched.goldPriceBankBuy ? formik.errors.goldPriceBankBuy : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isRow
                                disabled={formik.values.goldPriceBankBuyApi}
                            />
                        </div>

                        <div className={Styles['flex-div']}>
                            <Checkbox
                                label=''
                                isChecked={formik.values.goldPriceBankBuyApi}
                                onChange={() => {
                                    if (!formik.values.goldPriceBankBuyApi){
                                        formik.setFieldValue('goldPriceBankBuy', '');
                                        formik.setFieldTouched('goldPriceBankBuy', false);
                                    }

                                    formik.setFieldValue('goldPriceBankBuyApi', !formik.values.goldPriceBankBuyApi);
                                }}
                            />

                            <p>Use API</p>
                        </div>
                    </div>

                    <div>Price Bank Sell (RM)*</div>

                    <div className={Styles['flex-span-container']}>
                        <div>
                            <LabelledInput
                                id='goldPriceBankSell'
                                name='goldPriceBankSell'
                                label=':'
                                value={formik.values.goldPriceBankSell}
                                error={formik.touched.goldPriceBankSell ? formik.errors.goldPriceBankSell : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isRow
                                disabled={formik.values.goldPriceBankSellApi}
                            />
                        </div>

                        <div className={Styles['flex-div']}>
                            <Checkbox
                                label=''
                                isChecked={formik.values.goldPriceBankSellApi}
                                onChange={() => {
                                    if (!formik.values.goldPriceBankSellApi){
                                        formik.setFieldValue('goldPriceBankSell', '');
                                        formik.setFieldTouched('goldPriceBankSell', false);
                                    }

                                    formik.setFieldValue('goldPriceBankSellApi', !formik.values.goldPriceBankSellApi);
                                }}
                            />

                            <p>Use API</p>
                        </div>
                    </div>

                    <div>Affin Bank Buy Split ({formik.values.affinBankBuySplitToggle ? '%' : 'RM'})*</div>

                    <div className={Styles['flex-span-container']}>
                        <div>
                            <LabelledInput
                                id='affinBankBuySplit'
                                name='affinBankBuySplit'
                                label=':'
                                value={formik.values.affinBankBuySplit}
                                error={formik.touched.affinBankBuySplit ? formik.errors.affinBankBuySplit : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isRow
                            />
                        </div>

                        <div className={Styles['flex-div']}>
                            <label className={Styles['switch']}>
                                <input
                                    type='checkbox'
                                    checked={formik.values.affinBankBuySplitToggle}
                                    onChange={() => formik.setFieldValue('affinBankBuySplitToggle', !formik.values.affinBankBuySplitToggle)}
                                />
                                <span className={`${Styles['slider']} ${Styles['round']}`}></span>
                            </label>

                            <p>{formik.values.affinBankBuySplitToggle ? 'Percentage' : 'Fixed'}</p>
                        </div>
                    </div>

                    <div>TTT Bank Buy Split ({formik.values.tttBankBuySplitToggle ? '%' : 'RM'})*</div>

                    <div className={Styles['flex-span-container']}>
                        <div>
                            <LabelledInput
                                id='tttBankBuySplit'
                                name='tttBankBuySplit'
                                label=':'
                                value={formik.values.tttBankBuySplit}
                                error={formik.touched.tttBankBuySplit ? formik.errors.tttBankBuySplit : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isRow
                            />
                        </div>

                        <div className={Styles['flex-div']}>
                            <label className={Styles['switch']}>
                                <input
                                    type='checkbox'
                                    checked={formik.values.tttBankBuySplitToggle}
                                    onChange={() => formik.setFieldValue('tttBankBuySplitToggle', !formik.values.tttBankBuySplitToggle)}
                                />
                                <span className={`${Styles['slider']} ${Styles['round']}`}></span>
                            </label>

                            <p>{formik.values.tttBankBuySplitToggle ? 'Percentage' : 'Fixed'}</p>
                        </div>
                    </div>

                    <div>Affin Bank Sell Split ({formik.values.affinBankSellSplitToggle ? '%' : 'RM'})*</div>

                    <div className={Styles['flex-span-container']}>
                        <div>
                            <LabelledInput
                                id='affinBankSellSplit'
                                name='affinBankSellSplit'
                                label=':'
                                value={formik.values.affinBankSellSplit}
                                error={formik.touched.affinBankSellSplit ? formik.errors.affinBankSellSplit : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isRow
                            />
                        </div>

                        <div className={Styles['flex-div']}>
                            <label className={Styles['switch']}>
                                <input
                                    type='checkbox'
                                    checked={formik.values.affinBankSellSplitToggle}
                                    onChange={() => formik.setFieldValue('affinBankSellSplitToggle', !formik.values.affinBankSellSplitToggle)}
                                />
                                <span className={`${Styles['slider']} ${Styles['round']}`}></span>
                            </label>

                            <p>{formik.values.affinBankSellSplitToggle ? 'Percentage' : 'Fixed'}</p>
                        </div>
                    </div>

                    <div>TTT Bank Sell Split ({formik.values.tttBankSellSplitToggle ? '%' : 'RM'})*</div>

                    <div className={Styles['flex-span-container']}>
                        <div>
                            <LabelledInput
                                id='tttBankSellSplit'
                                name='tttBankSellSplit'
                                label=':'
                                value={formik.values.tttBankSellSplit}
                                error={formik.touched.tttBankSellSplit ? formik.errors.tttBankSellSplit : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isRow
                            />
                        </div>

                        <div className={Styles['flex-div']}>
                            <label className={Styles['switch']}>
                                <input
                                    type='checkbox'
                                    checked={formik.values.tttBankSellSplitToggle}
                                    onChange={() => formik.setFieldValue('tttBankSellSplitToggle', !formik.values.tttBankSellSplitToggle)}
                                />
                                <span className={`${Styles['slider']} ${Styles['round']}`}></span>
                            </label>

                            <p>{formik.values.tttBankSellSplitToggle ? 'Percentage' : 'Fixed'}</p>
                        </div>
                    </div>

                    <div>Minting Cost ({formik.values.mintingCostToggle ? '%' : 'RM'})*</div>

                    <div className={Styles['flex-span-container']}>
                        <div>
                            <LabelledInput
                                id='mintingCost'
                                name='mintingCost'
                                label=':'
                                value={formik.values.mintingCost}
                                error={formik.touched.mintingCost ? formik.errors.mintingCost : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                isRow
                            />
                        </div>

                        <div className={Styles['flex-div']}>
                            <label className={Styles['switch']}>
                                <input
                                    type='checkbox'
                                    checked={formik.values.mintingCostToggle}
                                    onChange={() => formik.setFieldValue('mintingCostToggle', !formik.values.mintingCostToggle)}
                                />
                                <span className={`${Styles['slider']} ${Styles['round']}`}></span>
                            </label>

                            <p>{formik.values.mintingCostToggle ? 'Percentage' : 'Fixed'}</p>
                        </div>
                    </div>

                    {/* <div>Alert Threshold (%)*</div>

                    <LabelledInput
                        id='lowGoldAlert'
                        name='lowGoldAlert'
                        label=':'
                        value={formik.values.lowGoldAlert}
                        error={formik.touched.lowGoldAlert ? formik.errors.lowGoldAlert : undefined}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isRow
                    /> */}

                </div>

                <ActionButton
                    label='Create Product'
                    type='submit'
                    className={Styles['submit-button']}
                    disabled={!formik.dirty}
                />
            </form>
        );
    };

    return (
        <div className={Styles.container}>
            {renderProductSettingForm()}
        </div>
    );
};

export default ProductSettingCreateProductForm;
